import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ToggleService {
  // Sidebar collapsed state. `true` = collapsed to the icon-only mini rail.
  // Seeded from localStorage so the user's choice survives reloads.
  private static readonly SIDEBAR_KEY = 'sidebarCollapsed'
  private sideBarSubject = new BehaviorSubject<boolean>(this.readSideBarState())
  private resetSubject = new BehaviorSubject<boolean>(false)
  // Transient (NOT persisted): the primary rail auto-collapses while a secondary
  // section panel is open, then restores the user's manual choice when it closes.
  private panelCollapseSubject = new BehaviorSubject<boolean>(false)
  /**
   * The off-canvas drawer, below the `lg` breakpoint only. Separate from
   * `sideBar$` on purpose: that one is a persisted DESKTOP preference, this one
   * is a transient MOBILE state that must always start closed — persisting it
   * would mean opening the app to a drawer covering the page. The topbar's
   * hamburger writes it and the sidebar reads it; nothing else should.
   */
  private mobileNavSubject = new BehaviorSubject<boolean>(false)
  sideBar$ = this.sideBarSubject.asObservable()
  reset$ = this.resetSubject.asObservable()
  panelCollapse$ = this.panelCollapseSubject.asObservable()
  mobileNav$ = this.mobileNavSubject.asObservable()

  constructor() { }

  private readSideBarState(): boolean {
    try {
      return localStorage.getItem(ToggleService.SIDEBAR_KEY) === 'true'
    } catch {
      return false
    }
  }

  /** Current collapsed state (synchronous) — lets consumers seed their UI. */
  get isSideBarCollapsed(): boolean {
    return this.sideBarSubject.value
  }

  setSideBarToggle(isToggle: boolean): void {
    try {
      localStorage.setItem(ToggleService.SIDEBAR_KEY, String(isToggle))
    } catch {
      // ignore storage failures (private mode / quota); state still lives in memory
    }
    this.sideBarSubject.next(isToggle)
  }

  setResetToggle(isReset: boolean): void {
    this.resetSubject.next(isReset)
  }

  /** Auto-collapse the primary rail while a secondary panel is open (transient). */
  setPanelCollapse(isCollapsed: boolean): void {
    this.panelCollapseSubject.next(isCollapsed)
  }

  get isMobileNavOpen(): boolean {
    return this.mobileNavSubject.value
  }

  setMobileNav(open: boolean): void {
    this.mobileNavSubject.next(open)
  }

  toggleMobileNav(): void {
    this.mobileNavSubject.next(!this.mobileNavSubject.value)
  }
}
